import React, { useState } from "react";
import { FileText, Download, ArrowRight, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/** Free Syllabus PDF download lead magnet — collects name/email/mobile and emails syllabus link.
 *  Renders an inline pill button + modal dialog. */
export default function FreeSyllabusCTA({ courseId, courseTitle }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name aur email zaroori hai");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads/syllabus-download", {
        ...form, course_id: courseId, course_title: courseTitle,
      });
      setDone(true);
      toast.success(data.message || "Syllabus sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 bg-white/5 hover:bg-[#FF0066]/15 border border-[#FF0066]/30 hover:border-[#FF0066]/60 text-white/90 hover:text-white text-sm font-medium rounded-full px-4 py-2 transition"
        data-testid="free-syllabus-btn"
      >
        <FileText className="w-4 h-4 text-[#FF0066]" /> Download free syllabus PDF
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-3 md:p-6" data-testid="syllabus-modal">
          <div className="relative w-full max-w-md bg-[#0a0a0a] border border-[#FF0066]/30 rounded-2xl shadow-2xl overflow-hidden">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-3 right-3 z-20 text-white/50 hover:text-white p-1 rounded-full bg-black/40 hover:bg-black/60" data-testid="syllabus-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            {!done ? (
              <form onSubmit={submit} className="p-6">
                <div className="w-12 h-12 rounded-full bg-[#FF0066]/15 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#FF0066]" />
                </div>
                <h3 className="mt-4 text-xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Get the <span className="text-[#FF0066]">{courseTitle}</span> syllabus
                </h3>
                <p className="mt-1.5 text-xs text-white/60">Curriculum · weekly breakdown · projects · placement support — emailed instantly.</p>
                <div className="mt-4 space-y-2.5">
                  <input type="text" placeholder="Your name *" required value={form.name}
                         onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                         className="w-full bg-[#050505] border border-white/15 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]"
                         data-testid="syllabus-name" />
                  <input type="email" placeholder="Email *" required value={form.email}
                         onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                         className="w-full bg-[#050505] border border-white/15 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]"
                         data-testid="syllabus-email" />
                  <input type="tel" placeholder="Mobile (optional)" value={form.mobile}
                         onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
                         className="w-full bg-[#050505] border border-white/15 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]"
                         data-testid="syllabus-mobile" />
                </div>
                <button type="submit" disabled={submitting}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-60 text-white font-semibold rounded-full px-4 py-3 text-sm transition"
                        data-testid="syllabus-submit">
                  {submitting ? "Sending…" : <><Download className="w-4 h-4" /> Send to my email</>}
                </button>
              </form>
            ) : (
              <div className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-500/15 mx-auto flex items-center justify-center">
                  <FileText className="w-7 h-7 text-emerald-300" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-white">Check your inbox!</h3>
                <p className="mt-1.5 text-sm text-white/65">Syllabus link sent to <b className="text-white">{form.email}</b>.</p>
                <a href={`https://wa.me/919131341638?text=${encodeURIComponent(`Hi, I downloaded the ${courseTitle} syllabus. Name: ${form.name}`)}`}
                   target="_blank" rel="noreferrer"
                   className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold rounded-full px-4 py-2.5 text-sm transition">
                  WhatsApp counsellor <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
